import * as React from "react"
import { useEffect, useState } from "react"
import {
  AudioWaveform,
  BookOpen,
  Bot,
  Command,
  Frame,
  GalleryVerticalEnd,
  Map,
  PieChart,
  Settings2,
  SquareTerminal,
  LucideIcon,
  Car,
  Receipt,
  CreditCard,
  Calendar,
  Bell,
  DollarSign,
  HandCoins,
  MapPin,
} from "lucide-react"
import { collection, getDocs } from "firebase/firestore"

import { NavMain } from "@/sidebar/nav-main"
import { NavBudgets } from "@/sidebar/nav-budgets"
import { NavUser } from "@/sidebar/nav-user"
import { NavFooter } from "@/sidebar/nav-footer"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { db } from "@/firebase"
import { useAuth } from "@/contexts/AuthContext"
import { NavigationProps, AppPage } from "@/types/navigation"

// Default data as fallback
const defaultData = {
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: SquareTerminal,
      items: [],
    },
    {
      title: "Calendar",
      url: "/calendar",
      icon: Calendar,
      items: [],
    },
    {
      title: "Transactions",
      url: "/transactions",
      icon: Receipt,
      items: [],
    },
    {
      title: "Subscriptions",
      url: "/subscriptions",
      icon: CreditCard,
      items: [],
    },
    {
      title: "Places",
      url: "/places",
      icon: MapPin,
      items: [],
    },
    {
      title: "Notifications",
      url: "/notifications",
      icon: Bell,
      items: [],
    },
  ],
}

// Helper function to get icon by name
const getIconByName = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    SquareTerminal,
    Bot,
    BookOpen,
    Settings2,
    Frame,
    PieChart,
    Map,
    AudioWaveform,
    Command,
    GalleryVerticalEnd,
    Car,
    Receipt,
    CreditCard,
    Calendar,
    Bell,
    DollarSign,
    HandCoins,
    MapPin,
  }
  return iconMap[iconName] || SquareTerminal
}

export function AppSidebar({ onNavigate, currentPage, ...props }: React.ComponentProps<typeof Sidebar> & NavigationProps & { currentPage?: AppPage }) {
  const [navMenuItems, setNavMenuItems] = useState(defaultData.navMain)
  const { currentUser } = useAuth()
  const { state: sidebarState } = useSidebar()

  // Effect to load sidebar data
  useEffect(() => {
    const CACHE_KEY = 'sidebar_menu_items'
    const CACHE_EXPIRY = 5 * 60 * 1000 // 5 minutes

    async function fetchNavMenuItems() {
      if (!currentUser) return

      try {
        // Check cache first
        const cachedData = localStorage.getItem(CACHE_KEY)
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData)
          const isExpired = Date.now() - timestamp > CACHE_EXPIRY
          
          if (!isExpired) {
            setNavMenuItems(data.map((item: { iconName?: string }) => ({
              ...item,
              icon: getIconByName(item.iconName || "SquareTerminal")
            })))
            return
          }
        }

        // Fetch from Firebase if no cache or expired
        if (db) {
          const menuItemsRef = collection(db, 'menuItems')
          const menuSnapshot = await getDocs(menuItemsRef)
          
          if (!menuSnapshot.empty) {
            const fetchedMenuItems = menuSnapshot.docs.map(doc => {
              const item = doc.data()
              return {
                title: item.title || "Menu Item",
                url: item.url || "#",
                icon: getIconByName(item.iconName || "SquareTerminal"),
                items: item.items || []
              }
            })

            // Update cache
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              data: fetchedMenuItems,
              timestamp: Date.now()
            }))

            setNavMenuItems(fetchedMenuItems)
          }
        }
      } catch (error) {
        console.error("Error fetching menu data:", error)
      }
    }

    fetchNavMenuItems()
  }, [currentUser])

  // Map menu items with active state based on current page
  const menuItemsWithActiveState = navMenuItems.map(item => ({
    ...item,
    isActive: currentPage === item.url.replace('/', '') as AppPage
  }))

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <NavUser onNavigate={onNavigate} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={menuItemsWithActiveState} onNavigate={onNavigate} />
        <NavBudgets onNavigate={onNavigate} currentPage={currentPage} sidebarState={sidebarState} />
      </SidebarContent>
      <SidebarFooter className="p-0">
        <NavFooter onNavigate={onNavigate} currentPage={currentPage} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
